import type {PageConsultation as importPageConsultation} from '@/uptk/core/components/moduleEntity/screen/lookupTypes';
/**
 * Module Spec v1 (UPT-2095) — TYPES D'ÉCHANGE entre Module Studio (web) et `/api/v1/module-spec/…` (rest).
 * Le document lui-même suit `module-spec.v1.schema.json` (forme canonique) ; il est transporté tel quel (`spec`).
 * UPT-2079 L14 (19.10.2026) : schéma de référence `module-spec.v1.2.schema.json` (habillage `presentation`, pages `custom`).
 * Web : copié dans `app/uptk/modules/module-studio/data/moduleSpecContracts.ts`. Rest : DTO aux mêmes noms de champs.
 */

export type IsoDateTime = string;

/**
 * Document Module Spec v1 (validé côté serveur par le schéma ; le front n'en lit que les sections qu'il affiche).
 * `1.1` (UPT-2096 §7.2, schéma `module-spec.v1.1.schema.json` publié le 13.10.2026 avec UPT-2095 §11.7) ajoute
 * `provenance.sourceArtifacts` et le bloc racine `rights` ; un document `1.0` reste valide. Le validateur n'enregistre
 * que la v1.1 (qui accepte « 1.0 »).
 * `1.2` (UPT-2079 L14, schéma `module-spec.v1.2.schema.json`) ajoute le bloc racine `presentation` et les pages `custom` (`prototypeRef`,
 * `reads`, `writes`, `layout`, `slots`, `actions`) ; le validateur enregistre alors la v1.2, qui accepte « 1.0 », « 1.1 » et « 1.2 ». La
 * normalisation du dépôt porte `specVersion` à « 1.2 » dès qu'une propriété v1.2 est présente (remarque `autofix` `spec.version_bumped`).
 */
export type ModuleSpecSpecVersion = '1.0' | '1.1' | '1.2' | '1.3' | '1.4' | '1.5' | '1.6' | '1.7' | '1.8' | '1.9' | '1.10' | '1.11';
export type ModuleSpecDocument = { specVersion: ModuleSpecSpecVersion; module: Record<string, unknown>; entities: Record<string, unknown> } & Record<string, unknown>;

/** Pièce source d'une version issue d'un import de prototype (v1.1, `provenance.sourceArtifacts[]`, ≤ 40). */
export interface ModuleSpecSourceArtifact {
  kind: 'html' | 'image' | 'snapshot' | 'prompt' | 'archive' | 'archive-entry' | 'data' | 'schema' | 'diagram' | 'document' | 'code' | 'spec' | 'module-base';
  /** Rôle de la pièce (lots 2 et suivants) ; facultatif. */
  role?: 'prototype' | 'prompt' | 'data' | 'schema' | 'mockup' | 'code' | 'document' | 'spec' | 'module-base' | 'reference';
  name?: string;
  /** SHA-256 (hex minuscule) de la pièce telle que stockée par Géode ; pour `prompt`, de l'UTF-8 du prompt stocké (jamais son texte). */
  sha256: string;
  sizeBytes?: number;
  /** uuid de l'import et de la pièce. */
  importRef?: string;
  pieceRef?: string;
  tool?: string;
  /** Extracteur `nom@version` (lots 2 et suivants). */
  extractor?: string;
}

/** Bloc racine `rights` (v1.1) : posé par le serveur, jamais repris d'une sortie de modèle. */
export interface ModuleSpecRights {
  businessSpecsOwner: { organisation: string; makerRef: string };
  generationOwner: 'geode';
  /** Version des conditions de droits (« draft-2026-10-11 » avant relecture juridique). */
  termsVersion: string;
  /** Intention déclarée du Maker, enregistrée, non appliquée dans ce lot. */
  derivation: 'none' | 'on-request' | 'standard';
  derivedFrom?: { moduleCode: string; version: string; ownerOrganisation: string; ownership: 'other' | 'same' }[];
}

/**
 * Source d'une version. `import` = dépôt par un humain d'un fichier Module Spec JSON/YAML (UPT-2095) ;
 * `prototype` = version issue de l'analyse d'un import de prototype (UPT-2096 P1-b), posée uniquement par le serveur
 * (jamais acceptée dans le corps d'un dépôt ni par MCP).
 */
export type ModuleSpecSource = 'human' | 'agent' | 'mcp' | 'import' | 'export' | 'prototype' | 'screens';
export type ModuleSpecStatus = 'proposed' | 'accepted' | 'rejected' | 'superseded';

export interface ValidationIssue {
  /**
   * Code de règle : schema, ref.field, ref.entity, unique, workflow, protection.pii, protection.demo, core.duplicate, i18n,
   * naming, screens, testplan.coverage, size ; UPT-2096 (amendement de UPT-2095) : rights.required, rights.derived,
   * rights.derived_dropped (erreurs), rights.derived_suspected (avertissement) ; UPT-2095 §11.7 : provenance.stripped
   * (avertissement : `provenance.sourceArtifacts` reçu dans un corps déposé, retiré par le serveur) ; UPT-2079 L11 §9 : autofix
   * (remarque `validation.infos`, jamais une erreur ni un avertissement : « corrigé automatiquement ») ; UPT-2079 L12 (avertissements du cycle de vie, jamais des
   * erreurs ; `path` = `/entities/<code>/workflow/...`, pour `workflow.transition_dropped` la transition du document REÇU) : `workflow.transition_dropped`
   * (transition retirée, état introuvable), `workflow.no_transitions` (au moins deux états, aucune transition), `workflow.dead_end` (état non final sans transition
   * sortante), `workflow.unreachable` (état inatteignable depuis l'état initial). UPT-2079 L14 (`rest/SPEC-UPT-2079-L14.md` §4.7) — erreurs :
   * `presentation.asset_missing`, `presentation.asset_duplicate`, `presentation.assets_too_large`, `page.target_unknown`, `page.transition_unknown`,
   * `page.slot_unknown`, `page.binding_entity`, `page.slug_taken` (et `ref.entity` pour les objets d'une page) ; avertissements : `presentation.contrast`,
   * `presentation.accent_state_like`, `presentation.asset_alt`, `page.custom_empty`, `page.prototype_ref`, `page.no_prototype_ref` ; remarques
   * (`validation.infos`) de l'extracteur et de l'analyse : `presentation.no_style`, `presentation.no_accent`, `presentation.font_fallback`,
   * `presentation.kept_manual`, `page.prototype_ref_dropped`, `page.trimmed`.
   */
  code: string;
  /** Chemin JSON (ex. /entities/loan/fields/4/protection). */
  path: string;
  message: string;
}

export interface ValidationReport {
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
  /** Scores de 0 à 100. */
  scores: { coverage: number; i18n: number; reuse: number; protection: number };
  stats: { entities: number; fields: number; relations: number; pages: number; scenes: number; steps: number };
  seedProjection: 'ok' | 'errors';
  /**
   * UPT-2079 L11 §9 : remarques « corrigé automatiquement » (code `autofix`), une par correction de la normalisation du dépôt ou de la réparation ;
   * jamais des erreurs ni des avertissements. Absent quand il n'y en a pas (anciennes versions, rapports du validateur).
   */
  infos?: ValidationIssue[];
}

/**
 * Projection portée par le dossier (`design_dossier.module_spec_json` / `module_spec_version` / `module_spec_status`,
 * UPT-2095 §11.1) : dernière version `accepted`, sinon dernière `proposed`, sinon aucune.
 */
export type ModuleSpecProjectionStatus = 'proposed' | 'accepted';

export interface ModuleSpecVersionSummary {
  /** Code du dossier (`dz-…`). */
  dossierRef: string;
  version: number;
  /** Version du schéma du document (« 1.0 », « 1.1 » ou « 1.2 »). */
  specVersion: ModuleSpecSpecVersion;
  moduleCode: string | null;
  source: ModuleSpecSource;
  status: ModuleSpecStatus;
  errorCount: number;
  warningCount: number;
  coverage: number;
  /** Auteur humain ou agent (« Claude Fable 5.1 via MCP », « GPT-6.1 Sol (assistant Géode) »…). */
  authoredBy: string;
  createdAt: IsoDateTime;
  decidedBy: string | null;
  decidedAt: IsoDateTime | null;
  /** UPT-2096 P1-b : uuid de l'import de prototype quand `source = prototype` (lien vers le rapport d'écarts), sinon absent ou null. */
  prototypeImportId?: string | null;
}

export interface ModuleSpecVersion extends ModuleSpecVersionSummary {
  spec: ModuleSpecDocument;
  checksum: string;
  validation: ValidationReport;
  decisionComment: string | null;
}

/** POST /dossiers/{dossierRef}/versions (UPT-2095 §11.1) : `source` absent = `human` ; toute autre valeur que `human` | `import` → 400 `modulespec.source_forbidden`. */
export interface ModuleSpecProposeInput {
  spec: ModuleSpecDocument;
  source?: 'human' | 'import';
  /** Commentaire libre du déposant (≤ 2 000 caractères), gardé avec la version, jamais interprété. */
  comment?: string;
}

/** POST /dossiers/{dossierRef}/versions/{version}/decision : `accept` exige zéro erreur (400 `modulespec.has_errors`) ; `reject` exige `comment` (400 `modulespec.comment_required`). */
export interface ModuleSpecDecisionInput {
  decision: 'accept' | 'reject';
  comment?: string;
}

/**
 * Réparer une version déjà proposée (spec web `SPEC-UPT-2079-L11.md` §9, rest : normalisation étendue de la vague 135).
 * `GET /dossiers/{dossierRef}/versions/{version}/repair` : aperçu, RIEN n'est écrit ;
 * `POST` sur la même route : applique la même normalisation au document de la version `version` (statut `proposed`) et crée une NOUVELLE version
 * `proposed` (source « réparation automatique », la version `version` reste inchangée) — réponse 201 de la forme de `POST …/versions`.
 * `GET` ne rend JAMAIS 409 : une version qui n'est pas `proposed`, ou sans correction possible, rend 200 avec `repairable: 0` (l'écran n'affiche alors pas le bouton).
 * Refus du `POST` : 409 `modulespec.repair.nothing` (rien de réparable) ; 409 `modulespec.not_proposed` (la version n'est plus `proposed`) ; 404 `modulespec.not_found` ;
 * droit identique à `propose` (`DESIGN_SPEC` CREATE ; l'aperçu demande READ). Un second `POST` sur la même version rend la version déjà créée (pas de doublon).
 * La version réparée garde la `source` et l'import de prototype de la version d'origine ; son `authoredBy` est suffixé « (réparation automatique) » et son rapport porte une
 * remarque `autofix` par correction (`validation.infos`).
 */
export type ModuleSpecRepairKind =
  | 'screens.list_columns'
  | 'screens.form_tabs'
  // L11 §9 : corrections mécaniques d'une erreur de schéma.
  | 'workflow.state_key'
  | 'page.description'
  | 'module.domain'
  | 'relation.duplicate'
  // L12 « Analyse en une passe » (rest §4.8) : le socle d'abord (objets, champs et liens du socle ; chemins du document REÇU),
  | 'core.entity_removed'
  | 'core.field_retyped'
  | 'core.field_removed'
  | 'core.relation_removed'
  | 'core.reuse_declared'
  | 'core.reference_cleaned'
  // les noms déjà pris (un objet renommé, une adresse d'objet précisée), et les transitions de workflow retrouvées par le nom de l'état.
  | 'entity.renamed'
  | 'entity.slugs_set'
  | 'workflow.transition_resolved'
  // Relecture rest du 18.10 : G4 — deux états de même clé : le second est suffixé ; R3 — un workflow sans deux états lisibles est retiré ;
  // G3 — un objet qui ressemble au socle mais est central (nom du module, cycle de vie, règles, liens) est conservé (`after` = le signe).
  | 'workflow.state_key_duplicate'
  | 'workflow.dropped'
  | 'core.candidate_kept'
  // L14 (rest §4.1) : version du schéma portée à « 1.2 » par une propriété v1.2 ; couleur de l'habillage ramenée en minuscules à six chiffres.
  | 'spec.version_bumped'
  | 'presentation.color_lowercased'
  | 'page.kind_other_migrated'
  | 'reference.identifier'
  | 'field.protection_typed'
  // Recette Change Management (25.10.2026) : une liaison de gabarit sans valeur à afficher redevient un texte à relier (réparation seulement).
  | 'template.binding_reset'
  // Finances v25 (25.10.2026) : une valeur de préremplissage devenue libellé traduisible redevient un texte (réparation seulement).
  | 'form.prefill_value'
  // Finances v29 (25.10.2026) : une clé d'action de page non conforme au schéma reçoit une clé tirée du libellé (réparation seulement).
  | 'page.action_key'
  // Passe réelle n° 3 de Finances (29.10.2026, GAP-31) : une décision « Repartir de la capture » journalisée à l'ancien format reçoit
  // ses empreintes SHA-256 (proposition et réparation).
  | 'journal.recapture_key';

export interface ModuleSpecRepairChange {
  /**
   * Chemin JSON de l'élément corrigé dans le document de la version avant réparation (ex. /entities/importjob/workflow/states/0, /pages/3/description, /module/domain, /relations/7).
   * L12 : l'objet d'un chemin `/entities/<code>` peut ne plus exister dans le document de la version CORRIGÉE (objet retiré ou renommé) : le code du chemin fait alors le nom.
   */
  path: string;
  /** Nature de la correction ; une nature inconnue de ce client (serveur plus récent) reste listée sous « autres corrections ». */
  kind: ModuleSpecRepairKind;
  /** Valeur avant et après, en texte lisible ; `before` vide = valeur absente, `after` vide = l'élément est retiré (relation en double identique). */
  before: string;
  after: string;
}

export interface ModuleSpecRepairPreview {
  version: number;
  /** Erreurs de la version avant la réparation, puis celles qu'il resterait (rapport recalculé sur le document réparé). */
  errorsBefore: number;
  errorsAfter: number;
  /** `errorsBefore − errorsAfter`, jamais négatif : les erreurs que la réparation fait disparaître (0 = rien à proposer ; le POST répondrait 409 `modulespec.repair.nothing`). */
  repairable: number;
  changes: ModuleSpecRepairChange[];
}

export interface ModuleSpecDiffSection {
  /** `rights` (UPT-2096, amendement de UPT-2095 §7.5 point 13) : bloc racine `rights` de la v1.1, dont `rights.derivation`
   *  de la version acceptée fait foi. */
  section: 'module' | 'entities' | 'fields' | 'relations' | 'pages' | 'navigation' | 'permissions' | 'workflow' | 'testPlan' | 'rights' | 'presentation' | 'other';
  added: string[];
  removed: string[];
  changed: string[];
}

/* ── Habillage et pages custom (UPT-2079 L14, schéma v1.2) ─────────────────────────────────────────────────────
 * Forme des blocs du document (le schéma `module-spec.v1.2.schema.json` fait foi) ; le web les lit dans le Concepteur (panneau Habillage,
 * éditeur des pages custom) et dans la simulation. Aucune couleur d'état dans une palette ; aucun HTML, aucune URL, aucun `data:`.
 */

/** Couleur opaque, hexadécimal minuscule à six chiffres (`^#[0-9a-f]{6}$`). */
export type ModuleSpecColor = string;

/** Palette de marque d'un mode ; clé absente = valeur du thème Géode de ce mode. */
export interface ModuleSpecPalette {
  accent: ModuleSpecColor;
  onAccent?: ModuleSpecColor;
  accentSoft?: ModuleSpecColor;
  surface?: ModuleSpecColor;
  surfaceRaised?: ModuleSpecColor;
  ink?: ModuleSpecColor;
  inkMuted?: ModuleSpecColor;
  border?: ModuleSpecColor;
}

/** Liste fermée des polices (rest §4.4 : `presentation-catalog.json`) ; `system*` = piles système. */
export const MODULE_FONT_FAMILIES = [
  'system', 'system-serif', 'system-mono', 'inter', 'roboto', 'open-sans', 'lato', 'montserrat', 'poppins', 'nunito',
  'source-sans-3', 'work-sans', 'ibm-plex-sans', 'noto-sans', 'merriweather', 'lora', 'playfair-display', 'cinzel', 'oswald',
  'rajdhani', 'orbitron', 'jetbrains-mono',
] as const;
export type ModuleFontFamily = (typeof MODULE_FONT_FAMILIES)[number];
export type ModuleFontWeight = 400 | 500 | 600 | 700;

export interface ModuleSpecFontRef {
  family: ModuleFontFamily;
  /** 1 à 4 poids uniques ; défaut [400, 700]. */
  weights?: ModuleFontWeight[];
}

export interface ModuleSpecAsset {
  key: string;
  role: 'illustration' | 'logo' | 'background' | 'icon' | 'photo' | 'decoration';
  /** Clé i18n ou texte par langue (`fr` obligatoire), comme tout libellé du document. */
  alt?: string | Record<string, string>;
  decorative?: boolean;
  /** SHA-256 des octets STOCKÉS par le dépôt de ressources du dossier (après ré-encodage). */
  sha256: string;
  mediaType: 'image/png' | 'image/jpeg';
  width: number;
  height: number;
  bytes: number;
  origin?: { kind: 'prototype-embedded' | 'prototype-image' | 'upload'; importRef?: string; pieceRef?: string };
  pages?: string[];
}

export interface ModuleSpecPresentation {
  skin?: {css:string;sha256:string};
  templateMessages?: Record<string,Record<string,string>>;
  components?: import('@/uptk/core/theme/component_recipes').ComponentRecipes;
  /** `prototype` = calculé par le serveur depuis la mesure de style du navigateur, jamais par l'IA ; défaut `manual`. */
  origin?: 'prototype' | 'manual';
  /** `nom@version` de l'extracteur (`style-probe@1`). */
  extractor?: string;
  baseScheme?: 'light' | 'dark';
  palette?: ModuleSpecPalette;
  paletteAlt?: ModuleSpecPalette;
  typography?: { heading?: ModuleSpecFontRef; body?: ModuleSpecFontRef } & import('@/uptk/core/theme/typography_roles').MeasuredTypography;
  shape?: {
    radius?: 'none' | 'sm' | 'md' | 'lg' | 'xl';
    shadow?: 'none' | 'sm' | 'md' | 'lg';
    density?: 'compact' | 'comfortable' | 'spacious';
    cardStyle?: 'flat' | 'outlined' | 'elevated' | 'glass';
  };
  applyTo?: 'all' | 'custom-pages';
  assets?: ModuleSpecAsset[];
  notes?: string;
}

/** Sélecteur simple d'une section du prototype : `#id`, `[data-x="v"]` ou `balise:nth-of-type(n)` (motif du schéma, revalidé partout). */
export const MODULE_SPEC_SECTION_SELECTOR =
  /^(?:#[A-Za-z][A-Za-z0-9_-]{0,79}|\[data-[a-z][a-z0-9-]{0,40}="[A-Za-z0-9_ -]{1,60}"\]|\[href="#[A-Za-z][A-Za-z0-9_-]{0,59}"\]|[a-z][a-z0-9]{0,9}:nth-of-type\([1-9][0-9]{0,2}\))(?: > (?:#[A-Za-z][A-Za-z0-9_-]{0,79}|\[data-[a-z][a-z0-9-]{0,40}="[A-Za-z0-9_ -]{1,60}"\]|\[href="#[A-Za-z][A-Za-z0-9_-]{0,59}"\]|[a-z][a-z0-9]{0,9}:nth-of-type\([1-9][0-9]{0,2}\))){0,7}$/;

export interface ModuleSpecPrototypeRef {
  entry?: { selector: string };
  importRef: string;
  pieceRef: string;
  selector?: string;
  sectionLabel?: string;
}

export interface ModuleSpecPageSlot {
  key: string;
  label: string | Record<string, string>;
  description?: string;
  entity?: string;
  binding?: 'none' | 'list' | 'record' | 'aggregate';
}

export interface ModuleSpecPageAction {
  nativeBinding?: import('@/uptk/core/components/moduleEntity/screen/nativeActionBindings').NativeActionBinding;
  key: string;
  label: string | Record<string, string>;
  kind: 'navigate' | 'create' | 'update' | 'transition' | 'custom';
  entity?: string;
  targetPage?: string;
  transition?: string;
  slot?: string;
  description?: string;
}

export type ModuleSpecPageKind = 'dashboard' | 'calendar' | 'cockpit' | 'wizard' | 'report' | 'board' | 'other' | 'custom';

/** Page du document (v1.2). Le générateur ne produit en squelette que `kind = 'custom'`. */
export interface ModuleSpecPage extends importPageConsultation {
  /** QUIZ-NATIVE2 GAP-Q14 : page dont celle-ci est une phase (même écran, autre état) ; jamais dans le menu. */
  phaseOf?: string;
  faithful?: import('@/uptk/core/components/moduleEntity/faithful/contracts').FaithfulReference;
  screen?: { header?: import('@/uptk/core/components/moduleEntity/screen/screenTypes').ScreenHeader; columns: 12; blocks: ModuleSpecBlock[] };
  captureRefs?: { sha256: string; selector?: string; label?: string }[];
  key: string;
  kind: ModuleSpecPageKind;
  label: string | Record<string, string>;
  description: string;
  slug?: string;
  portals?: ('portal' | 'adminportal')[];
  entities?: string[];
  sourceTemplate?: string;
  mockupRef?: string;
  prototypeRef?: ModuleSpecPrototypeRef;
  reads?: string[];
  writes?: string[];
  layout?: 'full' | 'centered' | 'split' | 'grid';
  slots?: ModuleSpecPageSlot[];
  actions?: ModuleSpecPageAction[];
}

/** Bornes v1.2 (schéma + validateur ; rest §3.1 et §4.8). */
export const MODULE_SPEC_V12_LIMITS = {
  assetsPerSpec: 16,
  assetMaxBytes: 524_288,
  assetsTotalMaxBytes: 2_097_152,
  assetMaxSide: 1920,
  assetsPerDossier: 32,
  slotsPerPage: 12,
  actionsPerPage: 20,
  readsWritesPerPage: 20,
  selectorMaxChars: 120,
  presentationNotesMaxChars: 1000,
} as const;

/* ── Assistant de conception (lot MS3, reporté le 13.10.2026 : UPT-2095 §11.2) ──────────────────────────────── */

/** `import` : analyse d'un import de prototype (UPT-2096 P1-b, `core.design_prototype_analysis`), jamais suivie d'un second avis. */
export type DesignTask = 'design' | 'review' | 'mechanical' | 'import';
/**
 * Voie IA (décision 9 du 13.10.2026) : clé personnelle de l'utilisateur, connexion du locataire, ou IA Géode en essai
 * plafonnée par le quota mensuel du locataire. Remplace les niveaux `premium | standard | economy` du 11.10.
 */
export type DesignRoute = 'user' | 'tenant' | 'geode';

export interface DesignEstimate {
  task: DesignTask;
  route: DesignRoute;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  /** Prix indicatif, non facturé tant que le plan Studio n'est pas tranché (décision 10 du 13.10). */
  clientPrice: number;
  currency: 'CHF' | 'EUR' | 'USD';
  /** Fourchette basse et haute (de la moitié au double). */
  range: { low: number; high: number };
  /** Coût fournisseur : présent seulement pour le locataire plateforme (UpTK Co). */
  providerCost?: number;
  /** Résidence non garantie (voie `user` : fournisseur choisi par l'utilisateur). */
  outsideEu: boolean;
  /** UPT-2096 P1-b : jetons d'image estimés d'après les pixels, déjà inclus dans `inputTokens` (informatif). */
  imageTokens?: number;
}

/** `stopped-quota` : quota d'essai du locataire épuisé pendant l'exécution (voie `geode` seulement). */
export type DesignRunStatus = 'running' | 'done' | 'stopped-quota' | 'refused' | 'failed';

export interface DesignRun {
  id: string;
  dossierRef: string;
  task: DesignTask;
  route: DesignRoute;
  model: string;
  status: DesignRunStatus;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  clientPrice: number;
  currency: 'CHF' | 'EUR' | 'USD';
  providerCost?: number;
  /** Numéro de la version Module Spec produite (core.module_spec_version.version), pas la version du schéma. */
  specVersion: number | null;
  /** UPT-2096 P1-b : uuid de l'import analysé quand `task = import`, sinon absent ou null. */
  prototypeImportId?: string | null;
  startedAt: IsoDateTime;
  finishedAt: IsoDateTime | null;
}

/*
 * `DesignBudget` (plafond par dossier, consentement Premium) est supprimé le 13.10.2026 (UPT-2095 §11.2) : le plafond est
 * le quota d'essai du locataire (`AiTrialQuota` de contracts/module-studio/contracts.ts) et la voie est `AiRoute`.
 */

/* Fidélité (F) — Module Spec v1.3. */
export const MODULE_SPEC_V13_LIMITS = { blocksPerScreen: 60, childrenPerBlock: 20, depth: 3, measures: 6, filters: 6, formulaChars: 200, components: 40 } as const;
export interface ModuleSpecFormat { kind: 'number' | 'currency' | 'percent' | 'months'; decimals?: number; currency?: 'CHF' | 'EUR' | 'USD'; unit?: string; compact?: boolean }
export interface ModuleSpecMeasure { mine?: boolean; key: string; op: 'count' | 'sum' | 'avg' | 'min' | 'max'; entity: string; field?: string; filters?: { field: string; operator: 'eq' | 'like' | 'gt' | 'lt'; value: string|{context:string} }[] }
export interface ModuleSpecBlock {
  template?: import('@/uptk/core/components/moduleEntity/template/templateTypes').NativeTemplate;
  filters?: import('@/uptk/core/components/dashboard/data/contextFilters').ContextFilter[];
  record?: import('@/uptk/core/components/moduleEntity/screen/lookupTypes').RecordDetail;
  externalLinks?: import('@/uptk/core/components/moduleEntity/screen/lookupTypes').ExternalLink[];
  variant?: string;
  tasks?: import('@/uptk/core/components/dashboard/data/types').DashboardBlock['tasks'];
  calculator?: import('@/uptk/core/components/moduleEntity/calc/calcModel').CalculatorConfig;
  questionnaire?: import('@/uptk/core/components/moduleEntity/calc/calcModel').QuestionnaireConfig;
  bindingReview?: {origin:'f12-safe'|'deduced'; justification:string; before:string; status:'applied'|'undone'};
  bindingCandidate?: {confidence:number; justification:string; verified:boolean; proposal:string};
 matrix?: import('@/uptk/core/components/moduleEntity/matrix/matrixModel').MatrixConfig;
  key: string; type: 'template' | 'calculator' | 'questionnaire' | 'matrix-quadrant' | 'matrix-grid' | 'matrix-cross' | 'kpi' | 'chart' | 'progress-list' | 'steps' | 'entity-list' | 'entity-form' | 'record-summary' | 'external-links' | 'text' | 'actions' | 'tabs' | 'stack' | 'grid' | 'banner' | 'image' | 'reference-image' | 'component' | 'code' | 'custom';
  title: Record<string, string>; description?: string; box?: { x: number; y: number; w: number; h: number }; mobile?: { order?: number; hidden?: boolean };
  bind?: { entity: string; mode: 'list' | 'record' | 'aggregate' | 'measures' }; measures?: ModuleSpecMeasure[];
  value?: { formula?: string; format?: ModuleSpecFormat }; caption?: { formula?: string; format?: ModuleSpecFormat; label?: Record<string, string> };

  subtitle?: Record<string,string>; icon?: string;
  subValues?: {key:string; label:Record<string,string>; formula:string; format?: ModuleSpecFormat}[];
  action?: {label:Record<string,string>; page:string};
  emphasis?: 'default' | 'hero' | 'accent'; chart?: { type: 'donut' | 'bar' | 'stacked-bar' | 'line'; groupBy: string; measure: string; limit?: number; other?: boolean };
  rows?: { labelField: string; valueField: string; targetField: string; limit?: number; offset?: number };
  list?: { columns?: { field: string; label?: Record<string, string> }[]; groupBy?: string; tabs?: { key: string; label: Record<string,string>; filter: { field:string; operator: 'eq'|'like'|'gt'|'lt'; value:string } }[] };
  children?: ModuleSpecBlock[]; componentRef?: { name: string; props: Record<string, unknown> }; captureRef?: { sha256: string; selector?: string }; code?: { path: string; artifactRef?: string; labels?: Record<string,Record<string,string>> };
  reason?: 'no_native_block' | 'needs_computed_field' | 'unsupported_interaction' | 'library_missing' | 'low_confidence'; prototypeRef?: { pieceRef: string; selector?: string; entry?: { selector: string } };
}

/** Champs calculés (F7), Module Spec v1.4. */
export interface ModuleSpecComputed { formula: string; scale?: number; emptyAs?: 'absent' | 'zero'; serverOnly?: boolean }
export interface ModuleSpecField { name: string; type: string; computed?: ModuleSpecComputed; [key: string]: unknown }
export const MODULE_SPEC_V14_LIMITS = { length: 300, depth: 8, operands: 16, fields: 20, chain: 3, sqlLength: 4000 } as const;
export const FORMULA_FUNCTIONS = ['min', 'max', 'abs', 'round', 'coalesce'] as const;
